"use client"

import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react'
import { Plus, X, Camera, ZoomIn } from 'lucide-react'
import { cn } from '@/lib/utils'
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { useTranslation } from 'react-i18next'
import { parseImagesFromFilesJson } from '@/utils/preprocess'
import { toast } from 'sonner'


interface LocalImage {
  name: string
  blob: Blob
  type: string
  url?: string // 可选的URL字段，用于直接显示图片
}

interface ImageUploadProps {
  className?: string
  onImagesChange?: (images: File[], allImages: ImageItem[]) => void
  maxImages?: number
  maxFileSize?: number // 新增：最大文件大小（字节），默认 50MB
  disabled?: boolean
  label?: string
  initialImages?: LocalImage[]
  isEditMode?: boolean // 新增：标识是否为编辑模式
  imageType?: 'question' | 'standardAnswer' | 'answer' // 新增：图片类型，用于确定命名规则
  // 新增：自动解析图片的属性
  filesJson?: string // 后端返回的图片JSON数据
  autoParseImages?: boolean // 是否自动解析图片
  backgroundText?: string // 背景文本
  // 样式变体：'default'=原通用样式（教师/管理端沿用）；'figma'=学生端答题区 Figma 隨心練 样式
  variant?: 'default' | 'figma'
  // 图片预览尺寸覆写(按实例隔离,不传则维持原 w-auto/h-32,教师端零影响)：
  // 学生端题目图小屏自适应用——把固定高框改成「按容器宽自适应 + 限高」,1920 段各自恢复原值
  previewClassName?: string // 覆写预览外框(默认 w-auto h-32)
  imgClassName?: string     // 覆写预览 <img>(默认 w-auto h-full)
}

interface ImageItem {
  file: File
  preview: string
  name: string
}

export function ImageUpload({
  className,
  onImagesChange,
  maxImages = 10,
  maxFileSize = 50 * 1024 * 1024, // 默认 50MB
  disabled = false,
  label,
  backgroundText,
  initialImages = [],
  isEditMode = false,
  imageType = 'question',
  filesJson,
  autoParseImages = false,
  variant = 'default',
  previewClassName,
  imgClassName
}: ImageUploadProps) {
  const { t } = useTranslation();

  // 自动解析图片数据
  const parsedImages = useMemo((): LocalImage[] => {
    if (autoParseImages && filesJson && isEditMode) {
      return parseImagesFromFilesJson(filesJson, imageType);
    }
    return initialImages;
  }, [autoParseImages, filesJson, isEditMode, imageType, initialImages]);

  const [images, setImages] = useState<ImageItem[]>([])
  const [showOptions, setShowOptions] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const initialImagesProcessedRef = useRef<string>('')
  const fileReadersRef = useRef<FileReader[]>([])

  // 生成图片名称，根据类型使用不同的前缀，使用数字命名
  const generateImageName = useCallback((index: number, fileExtension?: string): string => {
    const number = index + 1; // 从1开始计数：1, 2, 3, 4...
    const extension = fileExtension || ''; // 保留文件后缀名
    switch (imageType) {
      case 'question':
        return `q-${number}${extension}`; // q-1.jpg, q-2.png, q-3.jpeg...
      case 'standardAnswer':
        return `a-${number}${extension}`; // a-1.jpg, a-2.png, a-3.jpeg...
      case 'answer':
        return `${number}${extension}`; // 1.jpg, 2.png, 3.jpeg...
      default:
        return `${number}${extension}`;
    }
  }, [imageType])

  // 从现有图片名称中提取最大的索引
  const getMaxIndexFromImages = useCallback((imageList: ImageItem[]): number => {
    let maxIndex = -1;
    imageList.forEach(img => {
      let numberIndex = -1;
      switch (imageType) {
        case 'question':
          // 题目图片格式: "q-1.jpg"
          const qMatch = img.name.match(/q-(\d+)(?:\.[^.]+)?$/);
          if (qMatch) {
            numberIndex = parseInt(qMatch[1]) - 1; // 转换为0基索引
          }
          break;
        case 'standardAnswer':
          // 标准答案图片格式: "a-1.jpg"
          const sMatch = img.name.match(/a-(\d+)(?:\.[^.]+)?$/);
          if (sMatch) {
            numberIndex = parseInt(sMatch[1]) - 1; // 转换为0基索引
          }
          break;
        case 'answer':
          // 答案图片格式: "1.jpg", "2.png", "3.jpeg"...
          const aMatch = img.name.match(/^(\d+)(?:\.[^.]+)?$/);
          if (aMatch) {
            numberIndex = parseInt(aMatch[1]) - 1; // 转换为0基索引
          }
          break;
      }
      if (numberIndex >= 0) {
        maxIndex = Math.max(maxIndex, numberIndex);
      }
    });
    return maxIndex;
  }, [imageType])

  // 检查图片命名是否符合规范
  const isValidImageName = useCallback((name: string): boolean => {
    switch (imageType) {
      case 'question':
        return /^q-\d+(?:\.[^.]+)?$/.test(name);
      case 'standardAnswer':
        return /^a-\d+(?:\.[^.]+)?$/.test(name);
      case 'answer':
        return /^\d+(?:\.[^.]+)?$/.test(name);
      default:
        return false;
    }
  }, [imageType]);

  // 检查初始图片是否都符合命名规范
  const areInitialImagesValid = useCallback((initialImagesList: LocalImage[]): boolean => {
    if (initialImagesList.length === 0) return true;
    return initialImagesList.every(img => isValidImageName(img.name));
  }, [isValidImageName]);

  // 从原始fileJson中提取最大的索引（用于编辑模式）
  const getMaxIndexFromInitialImages = useCallback((initialImagesList: LocalImage[]): number => {
    // 如果初始图片命名不规范，返回-1，表示从头开始命名
    if (!areInitialImagesValid(initialImagesList)) {
      return -1;
    }

    let maxIndex = -1;
    initialImagesList.forEach(img => {
      let numberIndex = -1;
      switch (imageType) {
        case 'question':
          // 题目图片格式: "q-1.jpg"
          const qMatch = img.name.match(/q-(\d+)(?:\.[^.]+)?$/);
          if (qMatch) {
            numberIndex = parseInt(qMatch[1]) - 1; // 转换为0基索引
          }
          break;
        case 'standardAnswer':
          // 标准答案图片格式: "a-1.jpg"
          const sMatch = img.name.match(/a-(\d+)(?:\.[^.]+)?$/);
          if (sMatch) {
            numberIndex = parseInt(sMatch[1]) - 1; // 转换为0基索引
          }
          break;
        case 'answer':
          // 答案图片格式: "1.jpg", "2.png", "3.jpeg"...
          const aMatch = img.name.match(/^(\d+)(?:\.[^.]+)?$/);
          if (aMatch) {
            numberIndex = parseInt(aMatch[1]) - 1; // 转换为0基索引
          }
          break;
      }
      if (numberIndex >= 0) {
        maxIndex = Math.max(maxIndex, numberIndex);
      }
    });
    return maxIndex;
  }, [imageType, areInitialImagesValid])





  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || disabled) return

    const newImages: ImageItem[] = []
    const currentCount = images.length
    const rejectedFiles: string[] = []

    // 获取当前所有图片（包括初始图片和当前图片）的最大索引
    const maxIndexFromInitial = getMaxIndexFromInitialImages(parsedImages)
    const maxIndexFromCurrent = getMaxIndexFromImages(images)

    // 如果初始图片命名不规范，新增图片从0开始命名
    // 如果初始图片命名规范，新增图片从最大索引+1开始命名
    let nextIndex: number;
    if (!areInitialImagesValid(parsedImages)) {
      // 不规范命名：从0开始，但要避免与现有图片冲突
      nextIndex = Math.max(0, maxIndexFromCurrent + 1);
    } else {
      // 规范命名：从最大索引+1开始
      nextIndex = Math.max(maxIndexFromInitial, maxIndexFromCurrent) + 1;
    }

    Array.from(files).forEach((file) => {
      if (currentCount + newImages.length >= maxImages) {
        return
      }

      // 检查文件大小
      if (file.size > maxFileSize) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        rejectedFiles.push(`${file.name} (${fileSizeMB}MB)`);
        return;
      }

      if (file.type.startsWith('image/')) {
        const preview = URL.createObjectURL(file)

        // 提取文件扩展名
        const fileExtension = file.name.includes('.') ?
          '.' + file.name.split('.').pop() : '';

        // 使用递增的索引生成名称，确保名称不会重复且基于最大名称+1
        const name = generateImageName(nextIndex, fileExtension)
        nextIndex++ // 为下一个图片准备索引

        // 创建带有正确名称的 File 对象
        const renamedFile = new File([file], name, { type: file.type })

        const newImage = {
          file: renamedFile,
          preview,
          name
        }

        newImages.push(newImage)
      }
    })

    // 显示文件大小超限的错误提示
    if (rejectedFiles.length > 0) {
      const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(0);
      toast.error(t('component.fileerror') + `: ${rejectedFiles.join(', ')} ${t('common.error')} (${t('common.loading')} > ${maxSizeMB}MB)`);
    }

    if (newImages.length > 0) {
      const updatedImages = [...images, ...newImages]
      setImages(updatedImages)

      // 只发送新上传的图片文件，不包括已存在的图片
      const newUploadedFiles = updatedImages
        .filter(img => img.file.type !== 'image/url')
        .map(img => img.file) // 直接使用已经重命名的 file 对象

      onImagesChange?.(newUploadedFiles, updatedImages)
    }
  }, [images, maxImages, maxFileSize, disabled, onImagesChange, imageType, generateImageName, getMaxIndexFromImages, getMaxIndexFromInitialImages, parsedImages, areInitialImagesValid, t])

  const handleAddClick = () => {
    if (disabled || images.length >= maxImages) return
    setShowOptions(!showOptions)
  }

  const handleFileUpload = () => {
    setShowOptions(false)
    fileInputRef.current?.click()
  }

  const handleCameraClick = () => {
    setShowOptions(false)
    cameraInputRef.current?.click()
  }

  const handleRemoveImage = (index: number) => {
    if (disabled) return

    const imageToRemove = images[index]
    URL.revokeObjectURL(imageToRemove.preview)

    // 直接删除图片，不重新分配名称，保持名称稳定性
    const updatedImages = images.filter((_, i) => i !== index)

    setImages(updatedImages)
    // 只发送新上传的图片文件，不包括已存在的图片
    const newUploadedFiles = updatedImages
      .filter(img => img.file.type !== 'image/url')
      .map(img => img.file) // 直接使用已经重命名的 file 对象
    onImagesChange?.(newUploadedFiles, updatedImages)
  }

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index)
    setViewerOpen(true)
  }

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))
  }

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (disabled) return
    
    const files = e.dataTransfer.files
    handleFileSelect(files)
  }

  // 点击外部关闭选项
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // 检查点击是否在选项菜单外部
      const target = event.target as Element;
      if (target && !target.closest('.image-upload-options')) {
        setShowOptions(false);
      }
    };

    if (showOptions) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showOptions])

  // 处理初始图片
  useEffect(() => {
    // 创建一个简单的标识符来比较初始图片是否真的改变了
    const currentImagesSignature = parsedImages.map(img => `${img.name}-${img.type}-${img.url || ''}`).join(',');

    // 在编辑模式下，总是重新处理初始图片以确保刷新
    // 在创建模式下，防止重复处理相同的初始图片
    if (!isEditMode && initialImagesProcessedRef.current === currentImagesSignature) {
      return;
    }

    // 在编辑模式下，如果签名改变了或者是第一次加载，清空当前图片并重新处理
    if (isEditMode) {
      // 在编辑模式下，总是清空当前图片并重新处理，确保显示最新的 fileJson 数据
      
      setImages([]);
    }

    // 如果用户已经上传了图片，并且这不是第一次处理初始图片，则不要覆盖（仅在创建模式下）
    if (images.length > 0 && initialImagesProcessedRef.current !== '' && !isEditMode) {
      return;
    }

    // 清理之前的 FileReader
    fileReadersRef.current.forEach(reader => {
      reader.abort();
    });
    fileReadersRef.current = [];

    if (parsedImages.length > 0) {
      const imageItems: ImageItem[] = [];



      parsedImages.forEach((localImage, index) => {
        let preview: string;
        let file: File;
        // 处理图片命名逻辑
        let imageName: string;

        if (isEditMode && localImage.name) {
          // 编辑模式：始终保留原始名称
          imageName = localImage.name;
        } else if (localImage.name && !areInitialImagesValid(parsedImages)) {
          // 创建模式 + 初始图片命名不规范：保留不规范的原始名称
          imageName = localImage.name;
        } else {
          // 创建模式 + 初始图片命名规范（或没有初始图片）：生成规范的连续名称
          // 尝试从原始名称中提取扩展名，如果没有则根据类型推断
          let fileExtension = '';
          if (localImage.name && localImage.name.includes('.')) {
            fileExtension = '.' + localImage.name.split('.').pop();
          } else if (localImage.type) {
            // 根据MIME类型推断扩展名
            const typeMap: { [key: string]: string } = {
              'image/jpeg': '.jpg',
              'image/jpg': '.jpg',
              'image/png': '.png',
              'image/gif': '.gif',
              'image/webp': '.webp',
              'image/bmp': '.bmp',
              'image/svg+xml': '.svg'
            };
            fileExtension = typeMap[localImage.type] || '.jpg';
          }
          imageName = generateImageName(index, fileExtension);
        }

        // 检查是否有URL字段，如果有则直接使用URL作为预览
        if (localImage.url) {
          preview = localImage.url;
          // 对于URL类型的图片，需要异步下载并转换为File对象
          // 先创建一个占位符File对象，稍后会被实际的图片数据替换
          file = new File([localImage.blob], imageName, { type: 'image/url' });

          // 异步下载图片并更新File对象
          fetch(localImage.url)
            .then(response => response.blob())
            .then(blob => {
              // 创建新的File对象替换占位符
              const actualFile = new File([blob], imageName, { type: blob.type || 'image/jpeg' });

              // 更新images数组中对应的file对象
              setImages(prevImages => {
                const updatedImages = prevImages.map(img => {
                  if (img.name === imageName && img.file.type === 'image/url') {
                    return { ...img, file: actualFile };
                  }
                  return img;
                });

                // 触发回调，通知父组件图片已更新
                const allFiles = updatedImages.map(img => img.file);
                onImagesChange?.(allFiles, updatedImages);

                return updatedImages;
              });
            })
            .catch(error => {
              console.error('Failed to download image:', localImage.url, error);
            });
        } else if (localImage.blob instanceof Blob && localImage.blob.size > 0) {
          // 普通的blob图片 (skip empty placeholder blobs)
          preview = URL.createObjectURL(localImage.blob);
          file = new File([localImage.blob], imageName, { type: localImage.type });
        } else {
          // Skip images without a valid blob or url
          return;
        }

        
        imageItems.push({
          file: file,
          preview: preview,
          name: imageName
        });
      });

      
      setImages(imageItems);

      // 重要：触发onImagesChange回调，让父组件知道初始图片已加载
      // 对于初始图片，不发送任何文件（因为它们已经存在于后端）
      onImagesChange?.([], imageItems);

      initialImagesProcessedRef.current = currentImagesSignature;
    } else {
      // 如果没有初始图片，清空状态
      
      setImages([]);
      onImagesChange?.([], []);
      initialImagesProcessedRef.current = currentImagesSignature;
    }
  }, [parsedImages, isEditMode, imageType, areInitialImagesValid, generateImageName])

  // 清理预览 URL 和 FileReader
  useEffect(() => {
    return () => {
      images.forEach(img => URL.revokeObjectURL(img.preview));
      fileReadersRef.current.forEach(reader => {
        reader.abort();
      });
      fileReadersRef.current = [];
    }
  }, [])

  // 在不可編輯狀態下，如果沒有圖片則隱藏整個組件
  if (disabled && images.length === 0) {
    return null;
  }

  // 上传选项菜单（选择图片 / 拍摄），default 与 figma 两种 variant 共用
  // 手机横屏(≤500):「上傳輔助圖 +」按钮位于 277px 滚动面板(mobile-landscape-answer)的底部行,
  // 菜单原本 top-full 向下展开 —— absolute 元素不撑大 scroll 容器 scrollHeight,落到面板裁剪区外且无法滚动看到 → 被截断。
  // 改为向上展开(bottom-full),浮到上方书写区(空间充裕),留在可视 277px 内。桌面/平板(高>500)仍向下,不变。
  const optionsMenu = showOptions && (
    <div className="image-upload-options absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 w-max min-w-32 [@media(max-height:500px)]:top-auto! [@media(max-height:500px)]:bottom-full! [@media(max-height:500px)]:mt-0! [@media(max-height:500px)]:mb-1!">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); handleFileUpload(); }}
        className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg flex items-center gap-2 whitespace-nowrap"
      >
        <Plus className="w-4 h-4 shrink-0" />
        {t('component.chooseimg')}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); handleCameraClick(); }}
        className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg flex items-center gap-2 whitespace-nowrap"
      >
        <Camera className="w-4 h-4 shrink-0" />
        {t('component.shot')}
      </button>
    </div>
  );

  return (
    <div className="w-full">
      {label && (
        <div className="mb-2 text-sm font-medium text-gray-700">
          {label}：
        </div>
      )}
      <div
        className={cn(
          variant === 'figma'
            // Figma 答题上传：无外层灰底虚线框，方块直接置于白卡上
            // 手机横屏(≤500)去掉 pb-5,回收 20px 竖向空间(横屏面板高度吃紧)
            ? "w-full pb-5 [@media(max-height:500px)]:pb-0!"
            : cn(
                "w-full bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 p-3 pb-6",
                // 在不可編輯狀態下且有圖片時，調整樣式
                disabled && images.length > 0 ? "h-auto border-solid bg-transparent border-gray-200" : "min-h-[100px] h-auto",
                "transition-colors duration-200",
                !disabled && "hover:bg-gray-50 hover:border-gray-400",
              ),
          className
        )}
        onDragOver={!disabled ? handleDragOver : undefined}
        onDrop={!disabled ? handleDrop : undefined}
      >
      <div className="flex flex-wrap gap-2 items-start">
        {/* 添加按钮 - 只在可編輯狀態下顯示 */}
        {!disabled && images.length < maxImages && (
          variant === 'figma' ? (
            /* Figma 449:5604 Group 60: 上傳輔助圖 按钮 = 75×75 / r=10 / #D9D9D9 圆角方块 + 居中白色加号(28×28);
               标签「上傳輔助圖」15px / weight 330(font-light) / #939393, 位于按钮正下方 4-6px。
               小屏按断点缩放(60→70→75),保证笔记本不超大、1920 精准还原。 */
            <div className="flex flex-col items-center gap-1.5 [@media(max-height:500px)]:gap-0!">
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleAddClick(); }}
                  disabled={disabled}
                  /* EN-15：补键盘焦点环（只在 focus-visible 时出现，静态视觉不变） */
                  className="flex h-[60px] w-[60px] items-center justify-center rounded-[10px] bg-[#D9D9D9] transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A1C1D] focus-visible:ring-offset-2 xl:h-[70px] xl:w-[70px] 2xl:h-[75px] 2xl:w-[75px] [@media(max-height:500px)]:h-[49px]! [@media(max-height:500px)]:w-[49px]! [@media(max-height:500px)]:rounded-[4px]!"
                  aria-label={t('component.addImage', { defaultValue: '添加图片' })}
                >
                  <Plus className="size-6 text-white 2xl:size-7 [@media(max-height:500px)]:size-[18px]!" strokeWidth={2.5} />
                </button>
                {optionsMenu}
              </div>
              <span className="max-w-[96px] break-words text-center text-[13px] font-light leading-5 text-[#939393] 2xl:text-[15px] [@media(max-height:500px)]:mt-0.5! [@media(max-height:500px)]:text-[12px]! [@media(max-height:500px)]:leading-none!">
                {backgroundText || t('component.graphfile')}
              </span>
            </div>
          ) : (
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleAddClick()
                }}
                disabled={disabled}
                className={cn(
                  "flex items-center justify-center w-24 h-32 rounded-lg transition-colors duration-200",
                  "border-2 border-gray-400/40 hover:border-gray-500/40",
                  "bg-gray-400/60 hover:bg-gray-500/60",
                  disabled && "opacity-50 cursor-not-allowed hover:bg-gray-400/60 hover:border-gray-400/40"
                )}
                aria-label={t('component.addImage', { defaultValue: '添加图片' })}
              >
                <Plus className="w-8 h-8 text-white" />
                <span className='absolute top-0 left-0 right-0 bottom-0 p-2 w-full h-full text-center wrap-break-word text-xl text-gray-400/60'>{backgroundText || t('component.graphfile')}</span>
              </button>
              {optionsMenu}
            </div>
          )
        )}

        {/* 图片预览 */}
        {images.map((image, index) => (
          <div key={`${image.name}-${index}`} className="relative group">
            <div
              className={cn("w-auto h-32 rounded-lg overflow-hidden border-2 border-gray-300 bg-white cursor-pointer hover:border-gray-400 transition-colors", previewClassName)}
              onClick={() => handleImageClick(index)}
            >
              <img
                src={image.preview}
                alt={t('component.imagePreview', { defaultValue: '图片 {{name}}', name: image.name })}
                className={cn("w-auto h-full object-contain hover:scale-105 transition-transform", imgClassName)}
              />
              {/* 放大鏡圖標 */}
              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                <ZoomIn className="w-5 h-5 text-white" />
              </div>
            </div>

            {/* 图片名称标签 */}
            <div
              className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 text-gray-500 text-xs max-w-20 text-center"
              title={image.name} // 懸停顯示完整名稱
            >
              <span className="block truncate">
                {image.name}
              </span>
            </div>

            {/* 删除按钮 - 只在可編輯狀態下顯示 */}
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                /* EN-15：原来只有 hover 才显形，键盘用户永远看不到 → 补 focus-visible 显形 + 焦点环 */
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A1C1D] focus-visible:ring-offset-1 shadow-sm"
                aria-label={t('component.removeImage', { defaultValue: '删除图片 {{name}}', name: image.name })}
              >
                <X className="w-4 h-4 text-white" />
              </button>
            )}
          </div>
        ))}
      </div>



      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files)}
        disabled={disabled}
      />

      {/* 隐藏的摄像头输入 */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files)}
        disabled={disabled}
      />

      {/* 图片查看器 */}
      <DialogPrimitive.Root open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogPrimitive.Portal>
          {/* 自定义透明灰色遮罩 */}
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          />

          {/* 全屏内容容器 */}
          <DialogPrimitive.Content className="fixed inset-0 z-50 flex items-center justify-center outline-none">
            <div className="relative w-full h-full flex items-center justify-center">
              {images.length > 0 && (
                <>
                  {/* 主图片 */}
                  <img
                    src={images[currentImageIndex]?.preview}
                    alt={`图片 ${images[currentImageIndex]?.name}`}
                    className="w-auto max-w-[90vw] h-[80vh] object-contain"
                  />

                  {/* 图片信息 */}
                  <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg">
                    <span className="text-sm">
                      {t('component.img')} {images[currentImageIndex]?.name} ({currentImageIndex + 1} / {images.length})
                    </span>
                  </div>

                  {/* 导航按钮 */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={handlePrevImage}
                        className="absolute left-8 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-all border border-white border-opacity-30 hover:border-opacity-50"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={handleNextImage}
                        className="absolute right-8 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-all border border-white border-opacity-30 hover:border-opacity-50"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </>
                  )}

                  {/* 关闭按钮 */}
                  <button
                    onClick={() => setViewerOpen(false)}
                    className="absolute top-8 right-8 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all border border-white border-opacity-30 hover:border-opacity-50"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      </div>
    </div>
  )
}
